import { motion } from 'framer-motion'
import { useInView } from 'framer-motion'
import { useRef } from 'react'
import type { Technologies as TechnologiesType } from '../types/content'
import { Code, Layers, Smartphone, Database, GitBranch, Tool, Server, Cloud, Network, Shield, Briefcase, Brain } from './Icons'

interface TechnologiesProps {
  data: TechnologiesType
}

const iconMap: Record<string, React.ReactNode> = {
  code: <Code />,
  layers: <Layers />,
  smartphone: <Smartphone />,
  database: <Database />,
  'git-branch': <GitBranch />,
  tool: <Tool />,
  server: <Server />,
  cloud: <Cloud />,
  network: <Network />,
  shield: <Shield />,
  briefcase: <Briefcase />,
  brain: <Brain />,
}

export default function Technologies({ data }: TechnologiesProps) {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: '-100px' })

  return (
    <section id="technologies" className="section bg-dark-secondary" ref={ref}>
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="section-header"
        >
          <h2 className="section-title">{data.title}</h2>
          <p className="section-subtitle">{data.subtitle}</p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {data.categories.map((category, index) => (
            <motion.div
              key={category.name}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
              className="bg-dark border border-border rounded-xl p-6"
            >
              <div className="flex items-center gap-3 mb-4">
                <span className="text-primary">
                  {iconMap[category.icon] || <Code />}
                </span>
                <span className="text-lg font-semibold">{category.name}</span>
              </div>

              <div className="flex flex-wrap gap-2">
                {category.items.map(item => (
                  <span
                    key={item}
                    className="px-3 py-1 text-sm bg-dark-tertiary rounded-full text-gray-400"
                  >
                    {item}
                  </span>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}
